import { useRef, useState, useEffect } from 'react'

export default function SignaturePad({ onSign, onClose, documentName }) {
  const canvasRef = useRef(null)
  const drawing = useRef(false)
  const lastPos = useRef(null)
  const submissionInProgress = useRef(false)
  const [hasSignature, setHasSignature] = useState(false)
  const [mode, setMode] = useState('draw') // 'draw' | 'type'
  const [typedName, setTypedName] = useState('')
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState('')

  useEffect(() => {
    const canvas = canvasRef.current
    if (!canvas) return
    const ctx = canvas.getContext('2d')
    ctx.fillStyle = '#fff'
    ctx.fillRect(0, 0, canvas.width, canvas.height)
    ctx.strokeStyle = '#202326'
    ctx.lineWidth = 2.5
    ctx.lineCap = 'round'
    ctx.lineJoin = 'round'
    drawing.current = false
    setHasSignature(false)
    setError('')
  }, [mode])

  function getPos(e, canvas) {
    const rect = canvas.getBoundingClientRect()
    const scaleX = canvas.width / rect.width
    const scaleY = canvas.height / rect.height
    if (e.touches) {
      return {
        x: (e.touches[0].clientX - rect.left) * scaleX,
        y: (e.touches[0].clientY - rect.top) * scaleY
      }
    }
    return {
      x: (e.clientX - rect.left) * scaleX,
      y: (e.clientY - rect.top) * scaleY
    }
  }

  function startDraw(e) {
    if (submitting) return
    e.preventDefault()
    const canvas = canvasRef.current
    const pos = getPos(e, canvas)
    drawing.current = true
    lastPos.current = pos
    try { e.currentTarget.setPointerCapture(e.pointerId) } catch (_) {}
  }

  function draw(e) {
    e.preventDefault()
    if (!drawing.current || submitting) return
    const canvas = canvasRef.current
    const ctx = canvas.getContext('2d')
    const pos = getPos(e, canvas)
    if (Math.hypot(pos.x - lastPos.current.x, pos.y - lastPos.current.y) < 0.5) return
    ctx.beginPath()
    ctx.moveTo(lastPos.current.x, lastPos.current.y)
    ctx.lineTo(pos.x, pos.y)
    ctx.stroke()
    lastPos.current = pos
    setHasSignature(true)
    setError('')
  }

  function stopDraw(e) {
    e.preventDefault()
    drawing.current = false
  }

  function clearCanvas() {
    const canvas = canvasRef.current
    const ctx = canvas.getContext('2d')
    ctx.fillStyle = '#fff'
    ctx.fillRect(0, 0, canvas.width, canvas.height)
    drawing.current = false
    setHasSignature(false)
    setError('')
  }

  function generateTypedSignature() {
    const canvas = canvasRef.current
    const ctx = canvas.getContext('2d')
    ctx.fillStyle = '#fff'
    ctx.fillRect(0, 0, canvas.width, canvas.height)
    ctx.fillStyle = '#202326'
    ctx.font = 'italic 36px Georgia, serif'
    ctx.textAlign = 'center'
    ctx.textBaseline = 'middle'
    ctx.fillText(typedName.trim(), canvas.width / 2, canvas.height / 2, canvas.width - 24)
    setHasSignature(true)
  }

  async function handleSign() {
    if (!hasSignature || submissionInProgress.current) return
    submissionInProgress.current = true
    setSubmitting(true)
    setError('')
    try {
      if (typeof onSign !== 'function') throw new Error('Open a document to sign it.')
      await onSign(canvasRef.current.toDataURL('image/png'))
    } catch (err) {
      setError(err.message || 'Unable to save your signature. Please try again.')
    } finally {
      submissionInProgress.current = false
      setSubmitting(false)
    }
  }

  function close() {
    if (!submitting && typeof onClose === 'function') onClose()
  }

  return (
    <div className="signature-pad-root" style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24 }}
      onClick={e => { if (e.target === e.currentTarget) close() }}>
      <style>{`
        @media (max-width: 768px) {
          .signature-pad-root {
            padding: 12px !important;
          }
          .signature-pad-root .signature-modal {
            max-height: calc(100vh - 24px);
            overflow: auto;
          }
          .signature-pad-root .signature-modal-body {
            padding: 14px !important;
          }
          .signature-pad-root .signature-type-row {
            flex-direction: column;
          }
        }
      `}</style>
      <div className="card animate-fadeUp signature-modal" style={{ width: '100%', maxWidth: 520, padding: 0, overflow: 'hidden' }}>
        {/* Header */}
        <div style={{ background: '#202326', padding: '18px 24px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div>
            <div style={{ color: '#CBB17B', fontFamily: "'DM Sans', sans-serif", fontSize: 16, fontWeight: 700 }}>Sign Document</div>
            <div style={{ color: 'rgba(255,255,255,0.5)', fontSize: 11, marginTop: 2 }}>{documentName}</div>
          </div>
          <button onClick={close} disabled={submitting} aria-label="Close signature pad" style={{ background: 'none', border: 'none', color: 'rgba(255,255,255,0.5)', fontSize: 20, cursor: 'pointer' }}>×</button>
        </div>

        <div className="signature-modal-body" style={{ padding: '20px 24px' }}>
          {/* Mode toggle */}
          <div style={{ display: 'flex', background: '#F7F3EB', borderRadius: 8, padding: 3, marginBottom: 16, gap: 3 }}>
            {[['draw', '✍️ Draw Signature'], ['type', '⌨️ Type Signature']].map(([m, label]) => (
              <button key={m} onClick={() => setMode(m)} disabled={submitting} style={{
                flex: 1, padding: '7px', borderRadius: 6, border: 'none',
                fontSize: 12, fontWeight: 600, cursor: 'pointer',
                background: mode === m ? '#202326' : 'transparent',
                color: mode === m ? '#CBB17B' : '#62615D'
              }}>{label}</button>
            ))}
          </div>

          {/* Type mode input */}
          {mode === 'type' && (
            <div className="signature-type-row" style={{ display: 'flex', gap: 8, marginBottom: 12 }}>
              <input className="input" placeholder="Type your full name" value={typedName} disabled={submitting} onChange={e => { setTypedName(e.target.value); clearCanvas() }} style={{ flex: 1 }} />
              <button className="btn btn-ghost" onClick={generateTypedSignature} disabled={submitting || !typedName.trim()}>Apply</button>
            </div>
          )}

          {/* Canvas */}
          <div style={{ border: '2px solid #DED6CA', borderRadius: 8, overflow: 'hidden', marginBottom: 12, background: '#fff', cursor: mode === 'draw' ? 'crosshair' : 'default' }}>
            <canvas
              ref={canvasRef}
              width={472}
              height={160}
              style={{ display: 'block', width: '100%', height: 160, touchAction: 'none' }}
              onPointerDown={mode === 'draw' ? startDraw : undefined}
              onPointerMove={mode === 'draw' ? draw : undefined}
              onPointerUp={mode === 'draw' ? stopDraw : undefined}
              onPointerCancel={mode === 'draw' ? stopDraw : undefined}
            />
          </div>

          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
            <div style={{ fontSize: 11, color: '#62615D' }}>
              {mode === 'draw' ? 'Draw your signature above' : 'Type your name and click Apply'}
            </div>
            <button className="btn btn-ghost" style={{ fontSize: 11 }} onClick={clearCanvas} disabled={submitting}>Clear</button>
          </div>

          {/* Legal notice */}
          <div style={{ background: '#F7F3EB', borderRadius: 8, padding: '10px 14px', marginBottom: 16, fontSize: 11, color: '#62615D', lineHeight: 1.5 }}>
            🔒 By clicking <strong>Sign Document</strong>, you agree that your electronic signature is the legal equivalent of your handwritten signature and is binding on this document. Your signature, timestamp, and IP address will be recorded.
          </div>

          {error && <div role="alert" style={{ color: '#991B1B', marginBottom: 12, fontSize: 13 }}>{error}</div>}
          <div style={{ display: 'flex', gap: 10 }}>
            <button className="btn btn-ghost" onClick={close} disabled={submitting} style={{ flex: 1, justifyContent: 'center' }}>Cancel</button>
            <button className="btn btn-primary" onClick={handleSign} disabled={!hasSignature || submitting} style={{ flex: 2, justifyContent: 'center' }}>
              {submitting ? 'Saving…' : '✍️ Sign Document'}
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}
